import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderKanban, Mail, Trash2, UserPlus, Users } from '@/components/icons';
import { ROLE_LABELS, assignableOrgRoles, type OrgRole } from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { timeAgo } from '@/lib/format';
import type { Member } from '@/lib/types';
import { CopyField } from '@/components/report/CopyField';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Dialog,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  PageLoader,
  Select,
  useConfirm,
  useToast,
} from '@/components/ui';

const ROLE_HELP: Record<OrgRole, string> = {
  PROJECT_ADMIN: 'Creates projects, manages members, AI models and plugins.',
  MEMBER: 'Works on the projects you assign: tests, test data, runs and sharing reports.',
  VIEWER: 'Sees only reports shared with them and can comment on them.',
};

/** Checkbox list of the org's projects; each change assigns or unassigns right away. */
function AssignProjectsDialog({ orgId, member, onClose }: { orgId: string; member: Member | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const projects = useQuery({ queryKey: ['projects', orgId], queryFn: () => api.projects(orgId), enabled: !!member });
  const assigned = new Set((member?.projects ?? []).map((p) => p.id));

  const change = useMutation({
    mutationFn: ({ projectId, on }: { projectId: string; on: boolean }) =>
      on ? api.assignProjectMember(projectId, member!.userId).then(() => undefined) : api.unassignProjectMember(projectId, member!.userId),
    onSuccess: (_r, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['members', orgId] });
      qc.invalidateQueries({ queryKey: ['project-members', projectId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <Dialog
      open={!!member}
      onClose={onClose}
      title={`Projects for ${member?.name ?? ''}`}
      description="Project Members only see and work on the projects ticked here."
      footer={<Button onClick={onClose}>Done</Button>}
    >
      {projects.isPending ? (
        <PageLoader />
      ) : projects.isError ? (
        <ErrorNotice error={projects.error} onRetry={() => projects.refetch()} />
      ) : projects.data.length === 0 ? (
        <p className="text-sm text-slate-500">This organization has no projects yet. Create one on the Test projects page first.</p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-slate-200 dark:divide-slate-800 dark:ring-slate-700">
          {projects.data.map((p) => (
            <li key={p.id}>
              <label className="flex cursor-pointer items-center gap-3 px-4 py-3 text-sm">
                <input
                  type="checkbox"
                  className="size-4 accent-primary"
                  checked={assigned.has(p.id)}
                  disabled={change.isPending}
                  onChange={(e) => change.mutate({ projectId: p.id, on: e.target.checked })}
                />
                <span className="min-w-0 flex-1 truncate font-medium">{p.name}</span>
                <span className="truncate text-xs text-slate-400">{p.platform === 'WEB' ? p.targetUrl : p.appId}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

function InviteDialog({
  orgId,
  open,
  onClose,
  roles,
  initialProjectIds = [],
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
  roles: OrgRole[];
  /** Pre-ticked projects (e.g. "Invite to this project" from a project's Team tab). */
  initialProjectIds?: string[];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>('MEMBER');
  const [projectIds, setProjectIds] = useState<Set<string>>(new Set());
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const projects = useQuery({ queryKey: ['projects', orgId], queryFn: () => api.projects(orgId), enabled: open });
  const initialKey = initialProjectIds.join(',');

  useEffect(() => {
    if (open) setProjectIds(new Set(initialKey ? initialKey.split(',') : []));
  }, [open, initialKey]);

  const toggleProject = (id: string) =>
    setProjectIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const invite = useMutation({
    mutationFn: () => api.inviteMember(orgId, email.trim(), role, role === 'MEMBER' ? [...projectIds] : []),
    onSuccess: (res) => {
      setInviteUrl(res.inviteUrl);
      qc.invalidateQueries({ queryKey: ['org-invitations', orgId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const close = () => {
    setEmail('');
    setRole('MEMBER');
    setProjectIds(new Set());
    setInviteUrl(null);
    onClose();
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (email.trim()) invite.mutate();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title={inviteUrl ? 'Invitation ready' : 'Invite a teammate'}
      description={
        inviteUrl
          ? `Send this link to ${email}. It is valid for 7 days.`
          : 'They get access as soon as they accept. Project Members can open everything in the projects you choose and run the tests already there.'
      }
      footer={
        inviteUrl ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" form="invite-member" loading={invite.isPending} disabled={!email.trim()} icon={<Mail className="size-4" />}>
              Create invitation
            </Button>
          </>
        )
      }
    >
      {inviteUrl ? (
        <CopyField value={inviteUrl} label="invitation link" />
      ) : (
        <form id="invite-member" onSubmit={onSubmit} className="space-y-5">
          <Field label="Email address">
            {(id) => <Input id={id} type="email" autoFocus placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />}
          </Field>
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">Role</legend>
            <div className="grid gap-2">
              {roles.map((r) => (
                <label
                  key={r}
                  className="flex cursor-pointer items-start gap-3 rounded-xl p-3 ring-1 ring-slate-200 transition has-[:checked]:bg-brand-50 has-[:checked]:ring-2 has-[:checked]:ring-brand-500 dark:ring-slate-700 dark:has-[:checked]:bg-brand-900/30"
                >
                  <input type="radio" name="role" value={r} checked={role === r} onChange={() => setRole(r)} className="mt-1 accent-primary" />
                  <span>
                    <span className="block text-sm font-medium">{ROLE_LABELS[r]}</span>
                    <span className="block text-xs text-slate-500">{ROLE_HELP[r]}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          {role === 'MEMBER' && (
            <fieldset>
              <legend className="mb-1 text-sm font-medium text-slate-700 dark:text-slate-200">Projects</legend>
              <p className="mb-2 text-xs text-slate-500">
                They will see these projects' tests, test data, runs and reports, and can run the tests. You can change this later.
              </p>
              {projects.isPending ? (
                <PageLoader />
              ) : (projects.data ?? []).length === 0 ? (
                <p className="text-sm text-slate-500">No projects yet - you can assign them once you create one.</p>
              ) : (
                <ul className="max-h-56 divide-y divide-slate-100 overflow-auto rounded-xl ring-1 ring-slate-200 dark:divide-slate-800 dark:ring-slate-700">
                  {projects.data!.map((p) => (
                    <li key={p.id}>
                      <label className="flex cursor-pointer items-center gap-3 px-3.5 py-2.5 text-sm">
                        <input type="checkbox" className="size-4 accent-primary" checked={projectIds.has(p.id)} onChange={() => toggleProject(p.id)} />
                        <span className="min-w-0 flex-1 truncate">{p.name}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              {projectIds.size === 0 && (projects.data ?? []).length > 0 && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">Without a project they will not see any tests yet.</p>
              )}
            </fieldset>
          )}
        </form>
      )}
    </Dialog>
  );
}

export function MembersPage() {
  const { user, currentOrgId, currentOrgRole, can, memberships } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  // ?invite=1&project=<id> opens the invite dialog with that project ticked (from a project's Team tab).
  const [params, setParams] = useSearchParams();
  const [inviteOpen, setInviteOpen] = useState(params.get('invite') === '1');
  const invitedProject = params.get('project');
  const [assigning, setAssigning] = useState<Member | null>(null);
  const orgId = currentOrgId ?? '';
  const orgName = memberships.find((m) => m.orgId === orgId)?.orgName;

  const members = useQuery({ queryKey: ['members', orgId], queryFn: () => api.members(orgId), enabled: !!orgId });
  const canInvite = can('org.members.invite');
  const canManage = can('org.members.manage');
  const canAssign = can('project.members.manage');
  const orgProjects = useQuery({ queryKey: ['projects', orgId], queryFn: () => api.projects(orgId), enabled: !!orgId && canInvite });
  const invitations = useQuery({
    queryKey: ['org-invitations', orgId],
    queryFn: () => api.orgInvitations(orgId),
    enabled: !!orgId && canInvite,
  });

  const updateRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: OrgRole }) => api.updateMember(orgId, userId, role),
    onSuccess: () => {
      toast.success('Role updated');
      qc.invalidateQueries({ queryKey: ['members', orgId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => api.removeMember(orgId, userId),
    onSuccess: () => {
      toast.success('Member removed');
      qc.invalidateQueries({ queryKey: ['members', orgId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (!orgId) {
    return <EmptyState icon={<Users className="size-6" />} title="No organization selected" description="Create or join an organization first." />;
  }

  const roles = assignableOrgRoles(user?.platformRole ?? 'USER', currentOrgRole);
  const pending = (invitations.data ?? []).filter((i) => !i.acceptedAt && !i.revokedAt && new Date(i.expiresAt).getTime() > Date.now());
  const projectNames = new Map((orgProjects.data ?? []).map((p) => [p.id, p.name]));

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={orgName}
        title="Members"
        description="Invite Project Members and View Only users, and choose which projects each member works on."
        actions={
          canInvite && (
            <Button onClick={() => setInviteOpen(true)} icon={<UserPlus className="size-4" />}>
              Invite people
            </Button>
          )
        }
      />

      <Card>
        <CardHeader title="Team" description={members.data ? `${members.data.length} member${members.data.length === 1 ? '' : 's'}` : undefined} />
        {members.isPending ? (
          <PageLoader />
        ) : members.isError ? (
          <div className="p-5">
            <ErrorNotice error={members.error} onRetry={() => members.refetch()} />
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {members.data.map((m) => {
              const isMe = m.userId === user?.id;
              return (
                <li key={m.userId} className="flex flex-wrap items-center gap-4 px-5 py-3.5">
                  <span className="grid size-10 place-items-center rounded-full bg-slate-100 text-sm font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    {m.name.charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {m.name} {isMe && <span className="text-slate-400">(you)</span>}
                    </div>
                    <div className="truncate text-xs text-slate-500">{m.email}</div>
                    {m.role === 'MEMBER' && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {(m.projects ?? []).length === 0 ? (
                          <span className="text-xs text-amber-700 dark:text-amber-300">Not assigned to any project yet</span>
                        ) : (
                          m.projects!.map((p) => (
                            <Badge key={p.id} icon={<FolderKanban className="size-3" />}>
                              {p.name}
                            </Badge>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  {m.role === 'MEMBER' && canAssign && (
                    <Button variant="outline" size="sm" onClick={() => setAssigning(m)} icon={<FolderKanban className="size-4" />}>
                      Projects
                    </Button>
                  )}
                  {canManage && !isMe && m.role !== 'PROJECT_ADMIN' ? (
                    <>
                      <Select
                        aria-label={`Role for ${m.name}`}
                        value={m.role}
                        wrapperClassName="w-44"
                        onChange={(e) => updateRole.mutate({ userId: m.userId, role: e.target.value as OrgRole })}
                        options={roles.map((r) => ({ value: r, label: ROLE_LABELS[r] }))}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove ${m.name}`}
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Remove this member?',
                            message: `${m.name} will lose access to this organization's projects and reports. You can invite them again later.`,
                            confirmLabel: 'Remove member',
                          });
                          if (ok) remove.mutate(m.userId);
                        }}
                        icon={<Trash2 className="size-4 text-rose-500" />}
                      />
                    </>
                  ) : (
                    <Badge tone={m.role === 'PROJECT_ADMIN' ? 'brand' : 'neutral'}>{ROLE_LABELS[m.role]}</Badge>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {canInvite && pending.length > 0 && (
        <Card>
          <CardHeader title="Pending invitations" description="Waiting to be accepted" />
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {pending.map((i) => (
              <li key={i.id} className="flex items-center gap-4 px-5 py-3 text-sm">
                <Mail className="size-4 text-slate-400" />
                <span className="min-w-0 flex-1 truncate">{i.email}</span>
                {(i.projectIds ?? []).map((id) => projectNames.get(id)).filter(Boolean).map((name) => (
                  <Badge key={name} icon={<FolderKanban className="size-3" />}>
                    {name}
                  </Badge>
                ))}
                {i.orgRole && <Badge>{ROLE_LABELS[i.orgRole]}</Badge>}
                <span className="text-xs text-slate-500">sent {timeAgo(i.createdAt)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {canInvite && (
        <InviteDialog
          orgId={orgId}
          open={inviteOpen}
          initialProjectIds={invitedProject ? [invitedProject] : []}
          onClose={() => {
            setInviteOpen(false);
            if (params.has('invite')) setParams({}, { replace: true });
          }}
          roles={roles}
        />
      )}
      {canAssign && (
        <AssignProjectsDialog
          orgId={orgId}
          member={assigning && (members.data?.find((m) => m.userId === assigning.userId) ?? assigning)}
          onClose={() => setAssigning(null)}
        />
      )}
    </div>
  );
}
